import { createContext, useContext, ReactNode, useEffect, useState, useCallback } from "react";
import { useApp } from "./AppContext";

export interface ChatContact {
  id: string;
  name: string;
}

export interface ChatMessage {
  id: string;
  sender: "user" | "assistant";
  text: string;
  timestamp: number;
  senderId: string;
}

export interface ChatContextType {
  loading: boolean;
  error: string | null;
  contacts: ChatContact[];
  messages: Record<string, ChatMessage[]>;
  sendingMessage: boolean;

  // actions
  fetchContacts: () => void;
  sendMessage: (to: string, content: string, messageId: string) => void;
  clearError: () => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export function useChat() {
  const context = useContext(ChatContext);

  if (!context) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
}

export default function ChatProvider({ children }: { children: ReactNode }) {
  const { sessionReady, lastAuthTime, onMessage, sendMessage: appSendMessage } = useApp();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contacts, setContacts] = useState<ChatContact[]>([]);
  const [messages, setMessages] = useState<Record<string, ChatMessage[]>>({});
  const [sendingMessage, setSendingMessage] = useState(false);

  // ===== DATA FETCHING =====
  const fetchContacts = useCallback(() => {
    if (!sessionReady) {
      console.warn("[Chat] cannot fetch: session not ready");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      appSendMessage("chat", "get_data", {});
    } catch (err) {
      console.error("[Chat] failed to fetch:", err);
      setError("failed to fetch contacts");
      setLoading(false);
    }
  }, [sessionReady, appSendMessage]);

  // ===== MESSAGE HANDLERS =====
  const handleChatMessage = useCallback((message: any) => {
    // only handle chat service messages
    if (message.service !== "chat") return;

    if (message.action === "chat_data") {
      setContacts(message.payload.contacts || []);

      // load messages
      if (message.payload.messages) {
        console.log("[Chat] loaded messages:", message.payload.messages);

        const loadedMessages: Record<string, ChatMessage[]> = {};

        for (const contactId in message.payload.messages) {
          const chatData = message.payload.messages[contactId];
          loadedMessages[contactId] = chatData.messages.map((m: any, index: number) => ({
            id: `loaded-${index}-${Date.now()}`,
            sender: m.role === "user" ? "user" : "assistant",
            text: m.content,
            timestamp: Date.now() - (chatData.messages.length - index) * 60000, // approximate
            senderId: m.role === "user" ? "user" : contactId,
          }));
        }

        setMessages(loadedMessages);
      }

      setLoading(false);
      return;
    }

    // handle incoming message from contact
    if (message.action === "new_message") {
      const from = message.payload.from;
      const content = message.payload.content;
      const newMsg: ChatMessage = {
        id: `${Date.now()}-${Math.random()}`,
        sender: "assistant",
        text: content,
        timestamp: message.payload.timestamp || Date.now(),
        senderId: from,
      };

      setMessages((prev) => ({
        ...prev,
        [from]: [...(prev[from] || []), newMsg],
      }));

      setSendingMessage(false);
      return;
    }

    // chat error messages
    if (message.action === "error") {
      const errorMessage = message.payload?.message || "unknown error";
      console.error("[Chat] error:", errorMessage);
      setError(errorMessage);
      setSendingMessage(false);
      // auto-clear error after 5 seconds
      setTimeout(() => setError(null), 5000);
      return;
    }
  }, []);

  // send message to contact
  const sendMessage = useCallback(
    (to: string, content: string, messageId: string) => {
      if (!sessionReady) {
        console.warn("[Chat] cannot send: session not ready");
        return;
      }

      // optimistically add user message to ui
      const newMessage: ChatMessage = {
        id: messageId,
        sender: "user",
        text: content,
        timestamp: Date.now(),
        senderId: "user",
      };

      setMessages((prev) => ({
        ...prev,
        [to]: [...(prev[to] || []), newMessage],
      }));

      setSendingMessage(true);

      try {
        appSendMessage("chat", "send_message", { to, content, messageId });
      } catch (err) {
        console.error("[Chat] failed to send:", err);
        setError("failed to send message");
        setSendingMessage(false);
      }
    },
    [sessionReady, appSendMessage],
  );

  // clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // ===== SUBSCRIPTIONS =====
  useEffect(() => {
    const unsub1 = onMessage("chat_data", handleChatMessage);
    const unsub2 = onMessage("new_message", handleChatMessage);
    const unsub3 = onMessage("error", handleChatMessage);

    return () => {
      unsub1();
      unsub2();
      unsub3();
    };
  }, [onMessage, handleChatMessage]);

  // ===== AUTO-FETCH ON SESSION READY OR RECONNECT =====
  useEffect(() => {
    if (sessionReady) {
      fetchContacts();
    }
  }, [sessionReady, lastAuthTime, fetchContacts]);

  const value: ChatContextType = {
    loading,
    error,
    contacts,
    messages,
    sendingMessage,

    fetchContacts,
    sendMessage,
    clearError,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}
